import type { RoiId } from '~/domain/changes/ids';
import type { ContourRenderOptions } from '../../core/render/renderers/ContourRenderer';

/**
 * Alpha applied to regular contours while a margin/ring preview
 * is active. Keeps contours visible but visually recedes them
 * so the preview stands out. Only previews that opt in
 * ({@link CustomTargetObject.dimsOtherStructures}) apply it.
 */
export const PREVIEW_CONTOUR_ALPHA = 0.3;

/**
 * Alpha for non-selected contours while soft isolate is on. The renderer
 * multiplies contour RGB by this, so LOWER = darker. Must stay lighter than
 * {@link PREVIEW_CONTOUR_ALPHA}: isolate is a sustained working mode and the
 * neighbours must stay legible to contour against.
 */
export const ISOLATE_CONTOUR_ALPHA = 0.45;

/** The display states that can dim the contour layer, in priority order. */
export interface ContourDimmingState {
    /** A margin/ring/boolean preview is on screen. */
    readonly previewActive: boolean;
    /** That preview opted into receding the other contours. */
    readonly previewDimsOtherStructures: boolean;
    /** The user has switched on soft isolate. */
    readonly isolationActive: boolean;
}

/**
 * A preview outranks isolate — its own dimming governs the layer, including
 * when it opted out, so a preview that must not repaint the other structures
 * is not repainted by isolate instead. Isolate spotlights the selection, so it
 * dims only while the renderer actually draws one; a preview is itself the
 * thing at full strength and dims either way. Undefined = full opacity.
 */
export function resolveContourRenderOptions(
    state: ContourDimmingState
): ContourRenderOptions | undefined {
    if (state.previewActive) {
        return state.previewDimsOtherStructures
            ? { alpha: PREVIEW_CONTOUR_ALPHA }
            : undefined;
    }
    if (state.isolationActive) {
        return { alpha: ISOLATE_CONTOUR_ALPHA, dimsOnlyAroundSelection: true };
    }
    return undefined;
}

/**
 * The alpha the contour pass draws non-selected structures at, once it knows
 * whether a selected structure is present among those it has to draw. A mode
 * that spotlights the selection
 * ({@link ContourRenderOptions.dimsOnlyAroundSelection}) dims only while one
 * is: with nothing to hold at full intensity, every contour fading at once
 * reads as missing contours.
 */
export function resolveDimmedAlpha(
    options: ContourRenderOptions | undefined,
    selectionPresent: boolean
): number {
    if (options?.dimsOnlyAroundSelection && !selectionPresent) {
        return 1.0;
    }
    return options?.alpha ?? 1.0;
}

/**
 * True when the selected structure is the one being peeked. Null and
 * undefined name no structure.
 */
export function isSelectedStructurePeeked(
    selectedRoiId: RoiId | undefined,
    peekedRoiId: RoiId | null | undefined
): boolean {
    return peekedRoiId != null && selectedRoiId === peekedRoiId;
}

/** A structure the contour pass holds a drawable texture for. */
export interface ContourPassCandidate {
    readonly roiId: RoiId;
    /** It is the selected structure of its structure set. */
    readonly isSelected: boolean;
}

/** What the contour pass does with its candidates. */
export interface ContourPassPlan {
    /** Per candidate, in the candidates' order: whether its contour is drawn. */
    readonly drawn: readonly boolean[];
    /** The alpha the non-selected structures are drawn at. */
    readonly dimmedAlpha: number;
}

/**
 * Which of the structures with a drawable texture the contour pass draws,
 * given in draw order, and how dim the non-selected ones are.
 *
 * Only a selected structure is ever left out: by
 * {@link ContourRenderOptions.hideSelected}, or because it is the one
 * {@link ContourRenderOptions.peekedRoiId} names. A peeked structure that
 * belongs to no selected candidate leaves nothing out — a contour missing by
 * mistake misleads, a peek that fails to hide does not.
 *
 * The two omissions weigh differently on the dimming. A preview that hides
 * the selected structure has taken its place, so no selection is left to
 * spotlight. A peeked structure is still the one being worked on and is only
 * withheld from view, so it keeps counting as the selection: the other
 * contours look the same during the peek as before it, instead of brightening
 * because the selection is momentarily not drawn.
 */
export function resolveContourPass(
    options: ContourRenderOptions | undefined,
    candidates: readonly ContourPassCandidate[]
): ContourPassPlan {
    const { hideSelected = false, peekedRoiId } = options ?? {};
    return {
        drawn: candidates.map(
            ({ isSelected, roiId }) =>
                !isSelected ||
                !(hideSelected || isSelectedStructurePeeked(roiId, peekedRoiId))
        ),
        dimmedAlpha: resolveDimmedAlpha(
            options,
            !hideSelected && candidates.some(({ isSelected }) => isSelected)
        ),
    };
}
